import { usePaywall } from 'components/TimeTriggerPaywall/hooks/usePaywall'
import { useCurrentWorkspace } from './useCurrentWorkspace'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { useLoggedIn } from './useLoggedIn'
import { useFlipperFlag } from './useFlipperFlag'
import { useShowCreditsPaywall } from './useShowCreditsPaywall'

/**
 * Creates a function that either opens the paywall or executes the provided function
 * based on whether the user has access to paid features
 *
 * @param fn - The function to call if the user has access
 * @param options - Optional configuration
 * @param options.allowFreemium - If true, allows AP freemium workspaces to bypass the paywall
 * @returns A function with the same signature as the input function
 */
export const usePaywallGuard = <T extends (...args: any[]) => any>(
  fn?: T,
  options?: { allowFreemium?: boolean },
): T => {
  const workspace = useCurrentWorkspace()
  const isLoggedIn = useLoggedIn()
  const { openPaywall } = usePaywall()
  const isNewRegistration = useFlipperFlag('new_registration')
  const { showCreditsPaywall } = useShowCreditsPaywall()

  const hasFreemiumAccess = options?.allowFreemium && workspace?.is_ap_freemium

  const showPaywall =
    !hasFreemiumAccess &&
    isLoggedIn &&
    (!(workspace?.paying || workspace?.early_adopter) ||
      workspace?.id === ALMANAC_CORE_WORKSPACE_ID ||
      showCreditsPaywall) &&
    (isNewRegistration || workspace?.is_virtual_marketer)

  if (!fn) {
    return (() => undefined) as T
  }

  return ((...args: Parameters<T>) => {
    if (showPaywall) {
      openPaywall()
      return
    }

    return fn(...args)
  }) as T
}
